import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UnassignedOptimistic } from 'src/components/dispatch/unassignedOptimistic';
import { UnassignedItem } from 'src/entities/dispatch';

/** サーバにまだ反映されていない削除操作 1 回分 */
type PendingRemoval = {
  /** この操作で消した id */
  ids: ReadonlySet<string>;
  /** この操作で解除した選択。rollback で戻す */
  removedSelectedIds: string[];
  /** remove 時点の scope。変わっていたら rollback しない */
  scopeKey: string;
};

/**
 * /map の未割当リストの楽観削除。
 *
 * カード配車は `overrideUnassigned` という配列 state を上書きするが、/map の未割当は
 * `legRuns` からの useMemo 派生で setter が無い。そこで**配列を持たず、未確定の削除操作
 * (PendingRemoval) から表示を導出する**。復元用の配列を持たないので、rollback は
 * 「その操作を pendings から外す」だけで済み、`restoreRemovedItems`（配列を持つ側の関数）は要らない。
 *
 * Why not「サーバ更新があったら override を全部クリア」:
 *  - legRuns の参照比較は使えない。Container が flatMap を useMemo 無しで毎 render 評価するため、
 *    親の再描画だけで新しい配列になる（Map/index.tsx）
 *  - react-query の dataUpdatedAt も使えない。/map は deliveries / legRuns の両方で全ページを
 *    自動取得するので、削除の応答待ちの最中に無関係な次ページ取得で時刻が進む
 * そこで**操作単位で確定**する（対象 id が derived から全部消えた操作だけを外す）。
 */
export function useUnassignedOverride(params: {
  derivedItems: UnassignedItem[];
  scopeKey: string;
  selectedIds: ReadonlySet<string>;
  setSelectedIds: (updater: (prev: Set<string>) => Set<string>) => void;
}): { items: UnassignedItem[]; optimistic: UnassignedOptimistic; hasPendingRemoval: boolean } {
  const {
    derivedItems, scopeKey, selectedIds, setSelectedIds,
  } = params;

  const [pendings, setPendings] = useState<PendingRemoval[]>([]);

  // optimistic の参照を安定させるための ref。呼び出し側 (useDispatchDetailActions) が
  // useCallback の依存に入れるので、選択が変わるたびに作り直すと handler も作り直しになる。
  const selectedIdsRef = useRef(selectedIds);
  const setSelectedIdsRef = useRef(setSelectedIds);
  const scopeKeyRef = useRef(scopeKey);
  // 同期は render 中の代入ではなく useLayoutEffect で行う（render を副作用なしに保つ）。
  // 下の scope 用 useLayoutEffect より先に宣言し、scopeKeyRef の更新順を 1 箇所に保つ。
  useLayoutEffect(() => {
    selectedIdsRef.current = selectedIds;
    setSelectedIdsRef.current = setSelectedIds;
  }, [selectedIds, setSelectedIds]);

  // derivedItems が変わったら「消した id が derived から全部消えた操作」を state から外す。
  // 計算で除くだけでは state に残り続け、その id が再登場しても隠れたままになる。
  useEffect(() => {
    const derivedIds = new Set(derivedItems.map((i) => i.id));
    setPendings((prev) => {
      const next = prev.filter((p) => !Array.from(p.ids).every((id) => !derivedIds.has(id)));
      return next.length === prev.length ? prev : next;   // 同一参照を返して再描画を止める
    });
  }, [derivedItems]);

  // scope が変わったら全部捨てる。上の effect は derivedItems の変更でしか走らないので、
  // 参照が同じまま会社だけ変わると旧 pending が残り、案件が隠れ続ける。
  // useEffect だと切り替え直後の 1 フレームで旧 pending が効き、新しい会社・日付のリストから
  // 案件が 1 つ欠けて見えるため useLayoutEffect にする。
  useLayoutEffect(() => {
    scopeKeyRef.current = scopeKey;
    setPendings((prev) => (prev.length === 0 ? prev : []));
  }, [scopeKey]);

  // 表示は derived から「まだ確定していない操作の分」を引いたもの。
  // 隠すものが無ければ derivedItems の参照をそのまま返す（下流の memo を無駄に壊さない）。
  const items = useMemo(() => {
    if (pendings.length === 0) return derivedItems;
    const hiddenIds = new Set(pendings.flatMap((p) => Array.from(p.ids)));
    if (hiddenIds.size === 0) return derivedItems;
    const filtered = derivedItems.filter((i) => !hiddenIds.has(i.id));
    return filtered.length === derivedItems.length ? derivedItems : filtered;
  }, [derivedItems, pendings]);

  const optimistic = useMemo<UnassignedOptimistic>(() => ({
    remove: (removedItemIds: ReadonlySet<string>) => {
      const pending: PendingRemoval = {
        ids: removedItemIds,
        removedSelectedIds: Array.from(selectedIdsRef.current).filter((id) => removedItemIds.has(id)),
        scopeKey: scopeKeyRef.current,
      };
      setPendings((prev) => [...prev, pending]);
      setSelectedIdsRef.current((prev) => {
        const next = new Set(prev);
        removedItemIds.forEach((id) => next.delete(id));
        return next;
      });

      // commit / rollback は 1 操作につき 1 回だけ効かせる
      let settled = false;

      return {
        // この機構は「対象 id が derived から消えたら確定」なので、成功時にやることは
        // 「もう戻さない」と決めることだけ。表示側は useEffect が pending を外す。
        commit: () => { settled = true; },
        // rollback は「この操作を pendings から外す」だけ。items は derivedItems から
        // 導出されるので、外せば対象が自動的に再表示される。
        rollback: () => {
          // 旧 scope の案件を現在の日付・会社へ挿さない（選択にも混ぜない）
          if (settled || scopeKeyRef.current !== pending.scopeKey) return;
          settled = true;
          setPendings((prev) => {
            const next = prev.filter((p) => p !== pending);
            return next.length === prev.length ? prev : next;
          });
          setSelectedIdsRef.current((prev) => {
            const next = new Set(prev);
            pending.removedSelectedIds.forEach((id) => next.add(id));
            return next;
          });
        },
      };
    },
  }), []);

  return {
    items,
    optimistic,
    /** サーバ未反映の削除が 1 件でもあるか。運賃計算の可否判定に使う */
    hasPendingRemoval: pendings.length > 0,
  };
}
